import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';


const MonekPaymentForm = ({ model, editableAddress, isTest = false, embed = false, title }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);

  // Use process.env with hardcoded fallbacks in case .env is not yet loaded
  const MERCHANT_ID = process.env.REACT_APP_MONEK_MERCHANT_ID || "";
  const SECRET = process.env.REACT_APP_MONEK_SECRET || "";
  const ACCOUNT = process.env.REACT_APP_MONEK_ACCOUNT || "";
  const SERVICE_URL = process.env.REACT_APP_MONEK_SERVICE_URL || "";

  // Debugging: Log if variables are being picked from .env or fallback
  useEffect(() => {
    if (!process.env.REACT_APP_MONEK_MERCHANT_ID) {
      console.warn("MONEK: .env variables not found. Using fallback hardcoded values. Please restart your dev server.");
    } else {
      console.log("MONEK: Configuration loaded from .env successfully.");
    }
  }, []);

  // Destructure TotalToPay from model
  const { TotalToPay } = model || {};

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL}/rxp-hpp.js`;
    script.async = true;
    script.onload = () => initializeHpp();
    script.onerror = () => {
      setError('Failed to load payment library.');
      setLoading(false);
    };
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const sha1 = async (str) => {
    const enc = new TextEncoder();
    const hash = await crypto.subtle.digest('SHA-1', enc.encode(str));
    return Array.from(new Uint8Array(hash)).map(v => v.toString(16).padStart(2, '0')).join('');
  };

  const generateHppRequest = async () => {
    const timestamp = new Date().toISOString().replace(/[-:T\.Z]/g, "").slice(0, 14);
    const orderId = "ORD-" + Math.floor(Math.random() * 1000000);

    // Yahan TotalToPay use ho raha hai (£406.09 ko 40609 mein badalne ke liye)
    const amountVal = TotalToPay ? parseFloat(TotalToPay) : 0;
    const amount = Math.round(amountVal * 100).toString();

    const currency = "GBP"; // UK Pounds ke liye GBP use karein

    const toHash = `${timestamp}.${MERCHANT_ID}.${orderId}.${amount}.${currency}`;
    const firstHash = await sha1(toHash);
    const toSign = `${firstHash}.${SECRET}`;
    const sha1hash = await sha1(toSign);
  
    return {
        MERCHANT_ID, ACCOUNT, ORDER_ID: orderId, VAR_REF: model?.policyReference || '', AMOUNT: amount, CURRENCY: currency,
      TIMESTAMP: timestamp, SHA1HASH: sha1hash, AUTO_SETTLE_FLAG: "1",
      HPP_VERSION: "2", HPP_CHANNEL: "ECOM", HPP_LANG: "en", CARD_PAYMENT_BUTTON: "Pay Now",
      HPP_EXCLUDE_CARD_NETWORKS: "AMEX",
      HPP_BILLING_CITY: editableAddress?.address3 || model?.Address3 || '',
      HPP_BILLING_STREET1: editableAddress?.address1 || model?.Address1 || '',
      HPP_BILLING_POSTALCODE: editableAddress?.postcode || model?.Postcode || '',
      HPP_BILLING_COUNTRY: "826", 
      HPP_CUSTOMER_EMAIL: editableAddress?.email || model?.Email || '',
    };
  };

  const navigate = useNavigate();

  const responseCallback = (response, close) => {
    setPaymentResult(response);

    // Construct query parameters for the next page
    const params = new URLSearchParams({
      clientEmail: editableAddress?.email || model?.Email || '',
      name: model?.Name || '',
      address1: editableAddress?.address1 || model?.Address1 || 'Address1',
      address2: editableAddress?.address2 || model?.Address2 || 'Address2',
      address3: editableAddress?.address3 || model?.Address3 || 'Address3',
      address4: model?.Address4 || 'Address4',
      coverDate: model?.coverDate || '',
      policyReference: model?.Reference || '',
      postcode: editableAddress?.postcode || model?.Postcode || '',
      pReturn: model?.pReturn || '',
      pCode: model?.pCode || model?.Postcode || '',
      pRef: model?.pRef || model?.Reference || '',
      payment: model?.payment || '',
      insurer: model?.Insurer || '',
      schemeCode: model?.schemeCode || '',
      transactionamount: model?.TotalToPay || '',
      adminCharge: model?.currentInsurerCharge || '',
      legalPurchased: model?.legalPurchased || '',
      emergencyPurchased: model?.emergencyPurchased || '',
      keysPurchased: model?.keysPurchased || '',
      gadgetPurchased: model?.gadgetPurchased || '',
      phone: model?.phone || '',
      accountNumber: model?.accountNumber || '',
      sortCode: model?.sortCode || '',
      accountHolderDetails: model?.accountHolderDetails || '',
      // Payment Response Fields
      orderId: response?.ORDER_ID || '',
      authCode: response?.AUTHCODE || '',
      message: response?.MESSAGE || '',
      result: response?.RESULT || '',
      pasRef: response?.PASREF || '',
      merchantId: response?.MERCHANT_ID || '',
      timestamp: response?.TIMESTAMP || '',
      sha1hash: response?.SHA1HASH || ''
    }).toString();

    if (response && response.RESULT === "00") {
      setPaymentStatus('success');
      // Redirect to ThankYou page with parameters
      setTimeout(() => {
        navigate(`/thankyou/${model?.Reference}?${params}`);
      }, 2000); // Small delay to let user see success state
    } else {
      setPaymentStatus('error');
      // Redirect to UnSuccessful page with parameters
      setTimeout(() => {
        navigate(`/unsuccessful/${model?.Reference}?${params}`);
      }, 2000);
    }
    close();
    setLoading(false);
  };

  const initializeHpp = async () => {
    try {
      const jsonPayload = await generateHppRequest();
      window.RealexHpp.setHppUrl(SERVICE_URL);
      window.RealexHpp.embedded.init("autoload", "iframeId", responseCallback, jsonPayload);
      setLoading(false);
    } catch (err) {
      setError('Failed to generate payment configuration.');
      setLoading(false);
    }
  };

  const content = (
    <div className="checkout-container" style={{ background: 'white', borderRadius: '10px', width: '90%', maxWidth: '700px', textAlign: 'center', position: 'relative', margin: embed ? '0 auto' : undefined }}>
      {!paymentStatus && (
        <>
          {title && <h4 className='mb-3'>{title}</h4>}
          <p className='mb-4'>Amount to Pay: <b>£{TotalToPay}</b></p>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          <iframe id="iframeId" name="iframeId" title="Payment Frame" style={{ width: '100%', minHeight: '650px', border: 'none' }}></iframe>
        </>
      )}

    </div>
  );

  if (embed) {
    return content;
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {content}
    </div>
  );
};

export default MonekPaymentForm;